<template>
  <b-modal
    id="modal-request-compoff-balance"
    ref="modal-request-compoff-balance"
    title=""
    size="lg"
    modal-class="attendance-apply-modal request-compoff-modal"
    content-class="attendance-apply-modal-content"
    body-class="p-0"
    hide-footer
    hide-header
    centered
    @hidden="$emit('hidden')"
  >
    <div class="co-request-container">
      <b-row class="no-gutters">
        <!-- Left: Form -->
        <b-col lg="7" md="12" cols="12" class="co-form-section order-1">
          <div class="co-form-content">
            <div class="modal-flow-header">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="modal-flow-icon">
                    <feather-icon icon="PlusCircleIcon" size="18" />
                  </div>
                  <div>
                    <h5 class="mb-0 font-weight-bolder">Request Comp Off</h5>
                    <small class="text-muted">Credit CO for extra work day</small>
                  </div>
                </div>
                <b-button variant="flat-secondary" class="btn-icon p-50" @click="hide">
                  <feather-icon icon="XIcon" size="20" />
                </b-button>
              </div>

              <div class="balance-strip mt-2">
                <b-badge pill class="info-chip mb-0">
                  Today &amp; past work dates only
                </b-badge>
                <small class="text-muted ml-1 d-none d-sm-inline">
                  After HR approves, use Apply Comp Off
                </small>
              </div>
            </div>

            <b-form>
              <b-form-group
                v-if="(viewMode === 'team' || viewMode === 'org') && canManageLeaves && canApplyOnBehalf"
                label="Request on behalf of"
                label-for="selected-employee-compoff-balance"
                class="mb-2"
              >
                <b-form-select
                  id="selected-employee-compoff-balance"
                  v-model="compOffBalanceRequestForm.selected_employee"
                  :options="employeeOptions"
                >
                  <template #first>
                    <option :value="null">Myself (default)</option>
                  </template>
                </b-form-select>
              </b-form-group>

              <div class="date-summary-row mb-2">
                <div class="date-summary-cell">
                  <label class="date-summary-label" for="compoff-balance-work-date">Work date</label>
                  <b-form-input
                    id="compoff-balance-work-date"
                    :value="compOffBalanceRequestForm.work_date ? formatDateForDisplay(compOffBalanceRequestForm.work_date) : ''"
                    readonly
                    class="bg-light date-summary-input"
                    placeholder="—"
                  />
                </div>
              </div>
              <small class="text-muted d-block mb-2 date-hint">
                <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
                Select the weekend / holiday / OT day you worked
              </small>

              <b-form-group label="Reason" label-for="compoff-balance-reason" class="mb-2">
                <b-form-textarea
                  id="compoff-balance-reason"
                  v-model="compOffBalanceRequestForm.comment"
                  rows="3"
                  placeholder="Why you worked on this date and deserve CO credit"
                  required
                />
              </b-form-group>

              <div class="text-left mb-1">
                <b-button variant="link" size="sm" class="p-0" @click="$emit('view-requests')">
                  <feather-icon icon="ListIcon" size="14" class="mr-25" />
                  View my CO balance requests
                </b-button>
              </div>

              <div class="modal-flow-footer">
                <b-button variant="outline-secondary" size="sm" @click="hide">
                  Cancel
                </b-button>
                <b-button
                  variant="primary"
                  class="btn-attendance-primary"
                  @click="$emit('ok', $event)"
                >
                  <feather-icon icon="SendIcon" size="14" class="mr-50" />
                  Submit request
                </b-button>
              </div>
            </b-form>
          </div>
        </b-col>

        <!-- Right: Calendar (always visible) -->
        <b-col lg="5" md="12" cols="12" class="co-calendar-section order-2">
          <div class="co-calendar-content">
            <div class="calendar-header mb-2">
              <div class="d-flex justify-content-between align-items-center">
                <b-button variant="flat-primary" class="btn-icon p-25" @click="$emit('navigate-month', -1)">
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </b-button>
                <h6 class="mb-0 font-weight-bolder calendar-month-label">{{ calendarMonthYear }}</h6>
                <b-button variant="flat-primary" class="btn-icon p-25" @click="$emit('navigate-month', 1)">
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </b-button>
              </div>
            </div>
            <div class="calendar-wrapper">
              <div class="calendar-grid">
                <div class="calendar-weekdays">
                  <div v-for="(day, idx) in weekDayLabels" :key="`compoff-balance-work-weekday-${idx}`" class="weekday">{{ day }}</div>
                </div>
                <div class="calendar-days">
                  <div
                    v-for="(day, index) in calendarDays"
                    :key="`compoff-balance-work-day-${day.dateStr}-${index}`"
                    class="calendar-day-item"
                    :class="getDayClasses(day)"
                    :title="day.isDisabled ? 'Unavailable (future days blocked)' : ''"
                    @click="!day.isDisabled && $emit('select-date', day)"
                  >
                    {{ day.date }}
                  </div>
                </div>
              </div>
            </div>
            <div class="calendar-legend mt-2">
              <span class="legend-item"><span class="legend-swatch available" /> Selectable</span>
              <span class="legend-item"><span class="legend-swatch unavailable" /> Future / blocked</span>
              <span class="legend-item"><span class="legend-swatch selected" /> Selected</span>
            </div>
            <div v-if="compOffBalanceRequestForm.work_date" class="text-center mt-2">
              <b-button variant="outline-secondary" size="sm" @click="$emit('clear-selection')">
                <feather-icon icon="XIcon" size="12" class="mr-25" />
                Clear date
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BForm, BFormGroup, BFormSelect, BFormSelectOption,
  BFormInput, BFormTextarea, BButton, BBadge,
} from 'bootstrap-vue'

export default {
  name: 'RequestCompOffBalanceModal',
  components: {
    BModal, BRow, BCol, BForm, BFormGroup, BFormSelect, BFormSelectOption,
    BFormInput, BFormTextarea, BButton, BBadge,
  },
  props: {
    compOffBalanceRequestForm: { type: Object, required: true },
    showWorkCalendar: { type: Boolean, default: false },
    viewMode: { type: String, default: 'my' },
    canManageLeaves: { type: Boolean, default: false },
    canApplyOnBehalf: { type: Boolean, default: false },
    employeeOptions: { type: Array, default: () => [] },
    calendarMonthYear: { type: String, default: '' },
    calendarDays: { type: Array, default: () => [] },
    weekDayLabels: { type: Array, default: () => [] },
    getDayClasses: { type: Function, default: () => ({}) },
    formatDateForDisplay: { type: Function, default: (d) => d },
  },
  methods: {
    hide() {
      this.$bvModal.hide('modal-request-compoff-balance')
    },
  },
}
</script>

<style lang="scss" scoped>
$att-teal: #0099AC;
$att-teal-dark: #007a8a;
$att-border: #e5e7eb;
$att-bg-soft: #f3fafb;

.co-request-container {
  min-height: 280px;
  max-height: 78vh;
  overflow-y: auto;
}

.co-form-section {
  padding: 1rem 1.1rem 0.85rem;
  background: #fff;
  border-right: 1px solid $att-border;
}

.co-calendar-section {
  padding: 1rem;
  background: $att-bg-soft;
}

.modal-flow-header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid $att-border;
}

.modal-flow-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.65rem;
  background: rgba(0, 207, 232, 0.15);
  color: #00cfe8;
}

.balance-strip {
  background: #f8fafb;
  border: 1px solid $att-border;
  border-radius: 8px;
  padding: 0.4rem 0.55rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.info-chip {
  background: rgba(0, 207, 232, 0.15) !important;
  color: #0b8594 !important;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.35em 0.7em;
}

.date-summary-row {
  display: flex;
  gap: 0.5rem;
}

.date-summary-cell {
  flex: 1;
  min-width: 120px;
}

.date-summary-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

.date-summary-input {
  font-size: 0.85rem;
  min-height: 36px;
}

.date-hint {
  font-size: 0.72rem;
}

.modal-flow-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid $att-border;
}

.btn-attendance-primary {
  background: $att-teal !important;
  border-color: $att-teal !important;
  color: #fff !important;
  font-weight: 600;
}

.btn-attendance-primary:hover,
.btn-attendance-primary:focus {
  background: $att-teal-dark !important;
  border-color: $att-teal-dark !important;
}

.calendar-header {
  padding: 0.4rem 0.5rem;
  background: #fff;
  border: 1px solid $att-border;
  border-radius: 8px;
}

.calendar-month-label {
  color: $att-teal-dark;
  font-size: 0.9rem;
}

.calendar-wrapper {
  display: flex;
  justify-content: center;
}

.calendar-grid {
  width: 100%;
  max-width: 260px;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.2rem;
  margin-bottom: 0.35rem;
}

.weekday {
  text-align: center;
  font-weight: 600;
  font-size: 0.7rem;
  color: #6c757d;
  padding: 0.2rem 0;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.2rem;
}

.calendar-day-item {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.78rem;
  position: relative;
  color: #374151;
  min-height: 30px;
  border: 1px solid transparent;
  background: #fff;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.calendar-day-item:hover:not(.other-month):not(.selected-start):not(.disabled) {
  background: rgba(0, 153, 172, 0.1);
  border-color: rgba(0, 153, 172, 0.35);
}

.calendar-day-item.selected-start {
  background: $att-teal;
  border-color: $att-teal;
  color: #fff;
}

.calendar-day-item.other-month {
  opacity: 0.35;
  cursor: default;
}

.calendar-day-item.today:not(.selected-start):not(.disabled) {
  border-color: $att-teal;
  color: $att-teal-dark;
}

.calendar-day-item.disabled {
  cursor: not-allowed;
  background: #f3f4f6;
  color: #9ca3af;
}

.calendar-day-item.disabled::after {
  content: '';
  position: absolute;
  left: 18%;
  right: 18%;
  top: 50%;
  height: 1.5px;
  background: rgba(156, 163, 175, 0.85);
  transform: rotate(-28deg);
  pointer-events: none;
}

.calendar-day-item.holiday-disabled:not(.selected-start) {
  background: #fff8e6;
  color: #a16207;
}

.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.55rem;
  font-size: 0.68rem;
  color: #6b7280;
}

.legend-item {
  display: inline-flex;
  align-items: center;
}

.legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 0.3rem;
  display: inline-block;
  border: 1px solid transparent;
}

.legend-swatch.available {
  background: #fff;
  border-color: #d1d5db;
}

.legend-swatch.unavailable {
  background: #f3f4f6;
  box-shadow: inset 0 0 0 1px #d1d5db;
}

.legend-swatch.selected {
  background: $att-teal;
}

@media (max-width: 991.98px) {
  .co-form-section {
    border-right: none;
    border-bottom: 1px solid $att-border;
  }
}
</style>

<!-- b-modal teleports to body; unscoped so frame radius applies -->
<style lang="scss">
.attendance-apply-modal.request-compoff-modal .modal-content,
.attendance-apply-modal.request-compoff-modal .attendance-apply-modal-content {
  border-radius: 0.75rem;
  overflow: hidden;
}
</style>
